import React from "react";
import { PageHero, SectionHead, Marquee } from "../editorial/Layout";

const SERVICES = [
  { n: "01", t: "Plastering", short: "Walls, partitions, false ceilings", img: "/assets/firstAbout.jpg", tags: ["Plasterboard", "Acoustic", "Water-repellent", "Insulation"],
    body: "Installation of walls and false ceilings in classic, acoustic or water-repellent plasterboard, with thermal or acoustic insulation. We work with glass wool, rock wool, and rigid foam — chosen by what your wall actually needs, not by what we have in the truck." },
  { n: "02", t: "Electrical", short: "Switchboards, circuits, networks", img: "/assets/ele.jpg", tags: ["Up to standards", "Built-in", "Network", "Smart-home ready"],
    body: "Upgrade to standards, modification of the switchboard, creation of an electrical circuit — built-in or visible. Equipment, switches, computer network. Certified electricians, fully signed-off paperwork, ten-year guarantee on every line." },
  { n: "03", t: "Plumbing", short: "Baths, showers, kitchens, water heaters", img: "/assets/plumber.jpg", tags: ["Walk-in showers", "WC replacement", "Water heater", "Pipework"],
    body: "Creation and renovation of bathrooms or shower rooms, walk-in shower construction, WC and bath/shower cubicle replacement, water heater installation. Every joint pressure-tested before the wall closes." },
  { n: "04", t: "Tiling & marble", short: "Indoor and outdoor, any size", img: "/assets/marmura.jpg", tags: ["Large format", "Marble", "Mosaic", "Outdoor"],
    body: "Installation of tiles, wall tiles, marble or mosaic for indoor or outdoor use, in any size. From 30×30 ceramic to 320×160 porcelain slabs and book-matched Calacatta — we cut and lay everything on site." },
  { n: "05", t: "Painting", short: "Matt, satin, lime wash, decorative", img: "/assets/vopsit.jpg", tags: ["Matt", "Satin", "Lime wash", "Decorative"],
    body: "Our painters are trained in modern paint application techniques. Mineral, lime wash, decorative finishes — we colour-match on a 1:1 sample wall before committing to a full room." },
  { n: "06", t: "False ceilings", short: "Sloped or straight, with insulation", img: "/assets/parchet.jpg", tags: ["Rails", "Hangers", "Acoustic", "Glass wool"],
    body: "Sloped or straight, on rails or hangers in classic plasterboard, acoustic or water-repellent, with insulation (glass wool in roll or blown) — particularly useful in converted attics." },
];

const PREMIUM = [
  { n: "07", t: "Fitwall", d: "A panel of great resistance and hardness, characterised by its natural aesthetic, perfectly recreating volumes and textures with realistic designs — evoking wood, concrete, terracotta and textiles. Panel-form; surprising reliefs; highly durable for interior coverings, commercial and residential spaces, furniture.", tag: "Premium · Porcelanosa" },
  { n: "08", t: "XTone", d: "The PORCELANOSA brand focuses on a more natural and realistic design inspired by marble. Large-format pieces with marked, irregular veins which give the room an artistic atmosphere — highlighted by the porcelain stoneware and natural stone.", tag: "Premium · Porcelanosa" },
];

const OTHER = [
  { t: "Air conditioning", d: "Daikin-certified installation and maintenance." },
  { t: "Masonry", d: "Structural alterations, openings, supporting walls." },
  { t: "Aluminium & PVC", d: "Joinery — windows, doors, sliding systems." },
];

const SvcIndex = () => (
  <section className="ed-section">
    <style>{`
      .si-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
      .si-card { background: var(--paper); border-radius: 6px; overflow: hidden; cursor: pointer; transition: transform .25s; display: block; color: var(--ink); }
      .si-card:hover { transform: translateY(-3px); }
      .si-img { aspect-ratio: 5 / 3; background-size: cover; background-position: center; position: relative; }
      .si-img .n { position: absolute; top: 16px; left: 18px; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--paper); background: rgba(0,0,0,0.5); padding: 5px 10px; border-radius: 2px; letter-spacing: 0.12em; backdrop-filter: blur(6px); }
      .si-body { padding: 24px 26px 26px; }
      .si-body h3 { font-family: 'Fraunces', serif; font-weight: 400; font-size: 30px; letter-spacing: -0.02em; margin: 0 0 8px; }
      .si-body .s { font-size: 14px; color: var(--mute); }
      .si-body .arr { margin-top: 18px; display: inline-flex; align-items: center; gap: 8px; font-size: 13px; color: var(--ink); border-bottom: 1px solid var(--ink); padding-bottom: 2px; }
      @media (max-width: 960px) { .si-grid { grid-template-columns: 1fr; } }
    `}</style>
    <SectionHead
      num="01"
      eyebrow="Six core trades"
      title='Everything that <em>touches the wall</em>.'
      right="One studio, six core trades, two premium specialities, and a short list of certified partners for the rest. No subcontracted relay."
    />
    <div className="si-grid">
      {SERVICES.map((s, i) => (
        <a key={s.n} href={`#svc-${i}`} className="si-card">
          <div className="si-img" style={{ backgroundImage: `url(${s.img})` }}>
            <span className="n">{s.n}</span>
          </div>
          <div className="si-body">
            <h3>{s.t}</h3>
            <div className="s">{s.short}</div>
            <span className="arr">Read details →</span>
          </div>
        </a>
      ))}
    </div>
  </section>
);

const renderTitle = (t) =>
  t.includes(" ") ? (
    <>
      {t.split(" ")[0]} <em>{t.split(" ").slice(1).join(" ")}</em>
    </>
  ) : (
    t
  );

const SvcDetail = () => (
  <section className="ed-section">
    <style>{`
      .sd-row { display: grid; grid-template-columns: 80px 1fr 1.1fr; gap: 60px; padding: 64px 0; border-top: 1px solid var(--line); align-items: start; }
      .sd-row:first-child { border-top: 0; padding-top: 0; }
      .sd-num { font-family: 'JetBrains Mono', monospace; font-size: 13px; color: var(--mute); letter-spacing: 0.16em; padding-top: 12px; }
      .sd-text h3 { font-family: 'Fraunces', serif; font-weight: 400; font-size: 56px; letter-spacing: -0.025em; line-height: 1; margin: 0 0 20px; }
      .sd-text h3 em { font-style: italic; color: var(--clay); }
      .sd-text .short { font-size: 15px; color: var(--mute); margin-bottom: 26px; }
      .sd-text .body { font-size: 15.5px; line-height: 1.6; color: var(--ink-2); margin-bottom: 26px; max-width: 540px; }
      .sd-tags { display: flex; flex-wrap: wrap; gap: 8px; }
      .sd-tags .t { font-size: 12px; padding: 6px 12px; border-radius: 999px; border: 1px solid var(--line); color: var(--ink); letter-spacing: 0.02em; background: var(--paper); }
      .sd-img { aspect-ratio: 4 / 5; background-size: cover; background-position: center; border-radius: 6px; position: relative; overflow: hidden; }
      .sd-img .cap { position: absolute; left: 16px; bottom: 14px; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--paper); letter-spacing: 0.16em; background: rgba(0,0,0,0.5); padding: 6px 10px; backdrop-filter: blur(6px); border-radius: 2px; text-transform: uppercase; }
      @media (max-width: 960px) { .sd-row { grid-template-columns: 1fr !important; gap: 24px; } .sd-text h3 { font-size: 36px; } }
    `}</style>
    <SectionHead
      num="02"
      eyebrow="In detail"
      title='Six core trades,<br/><em>told properly</em>.'
      right="Everything below is done in-house by our own staff or by certified contractors we've worked with for at least three years. No first-time crews on your home."
    />
    {SERVICES.map((s, i) => {
      const reversed = i % 2 !== 0;
      return (
        <div
          key={s.n}
          id={`svc-${i}`}
          className="sd-row"
          style={reversed ? { gridTemplateColumns: "80px 1.1fr 1fr" } : undefined}>
          <div className="sd-num">{s.n}</div>
          {reversed ? (
            <>
              <div className="sd-img" style={{ backgroundImage: `url(${s.img})` }}>
                <span className="cap">Service Nº {s.n} · {s.t}</span>
              </div>
              <div className="sd-text">
                <h3>{renderTitle(s.t)}</h3>
                <div className="short">{s.short}</div>
                <p className="body">{s.body}</p>
                <div className="sd-tags">{s.tags.map((t) => <span key={t} className="t">{t}</span>)}</div>
              </div>
            </>
          ) : (
            <>
              <div className="sd-text">
                <h3>{renderTitle(s.t)}</h3>
                <div className="short">{s.short}</div>
                <p className="body">{s.body}</p>
                <div className="sd-tags">{s.tags.map((t) => <span key={t} className="t">{t}</span>)}</div>
              </div>
              <div className="sd-img" style={{ backgroundImage: `url(${s.img})` }}>
                <span className="cap">Service Nº {s.n} · {s.t}</span>
              </div>
            </>
          )}
        </div>
      );
    })}
  </section>
);

const SvcPremium = () => (
  <section className="ed-section" style={{ background: "var(--ink)", color: "var(--paper)" }}>
    <style>{`
      .sp-head .num { color: rgba(251,248,242,0.5) !important; }
      .sp-head h2 { color: var(--paper) !important; }
      .sp-head h2 em { color: var(--sand) !important; }
      .sp-head .h2-r { color: rgba(251,248,242,0.75) !important; }
      .sp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-top: 24px; }
      .sp-card { border: 1px solid rgba(251,248,242,0.18); border-radius: 6px; padding: 36px 32px; background: rgba(251,248,242,0.03); }
      .sp-card .tag { display: inline-block; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--clay); letter-spacing: 0.14em; text-transform: uppercase; padding: 5px 10px; border: 1px solid var(--clay); border-radius: 999px; margin-bottom: 22px; }
      .sp-card h3 { font-family: 'Fraunces', serif; font-weight: 300; font-size: 64px; letter-spacing: -0.03em; line-height: 1; margin: 0 0 22px; color: var(--paper); }
      .sp-card h3 em { font-style: italic; color: var(--sand); }
      .sp-card p { font-size: 15px; line-height: 1.6; color: rgba(251,248,242,0.78); margin: 0; }
      .sp-card .n { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: rgba(251,248,242,0.4); letter-spacing: 0.16em; }
      @media (max-width: 960px) { .sp-grid { grid-template-columns: 1fr; } .sp-card h3 { font-size: 40px; } }
    `}</style>
    <div className="sp-head">
      <SectionHead
        num="03"
        eyebrow="Premium services"
        title='Two materials we know<br/><em>by heart</em>.'
        right="We're certified installers for Porcelanosa's large-format porcelain (XTone) and Fitwall's high-resistance panels. They define what we can do with surfaces."
      />
    </div>
    <div className="sp-grid">
      {PREMIUM.map((p) => (
        <div key={p.n} className="sp-card">
          <span className="tag">{p.tag}</span>
          <h3>{p.t === "XTone" ? <>X<em>Tone</em></> : <>Fit<em>wall</em></>}</h3>
          <p>{p.d}</p>
          <div className="n" style={{ marginTop: 28, paddingTop: 22, borderTop: "1px solid rgba(251,248,242,0.15)" }}>
            Service Nº {p.n}
          </div>
        </div>
      ))}
    </div>
  </section>
);

const SvcOther = () => (
  <section className="ed-section">
    <style>{`
      .so-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; border-top: 1px solid var(--ink); }
      .so-cell { padding: 32px 32px 36px; border-right: 1px solid var(--line); }
      .so-cell:last-child { border-right: 0; }
      .so-cell h3 { font-family: 'Fraunces', serif; font-weight: 400; font-size: 28px; letter-spacing: -0.02em; margin: 0 0 10px; }
      .so-cell p { font-size: 14.5px; line-height: 1.55; color: var(--ink-2); margin: 0; }
      .so-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 24px; }
      .so-head .num { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--mute); letter-spacing: 0.18em; text-transform: uppercase; }
      .so-head h2 { font-family: 'Fraunces', serif; font-weight: 300; font-size: 56px; letter-spacing: -0.025em; margin: 0; }
      .so-head h2 em { font-style: italic; color: var(--clay); }
      @media (max-width: 960px) { .so-grid { grid-template-columns: 1fr; } .so-head h2 { font-size: 36px; } }
    `}</style>
    <div className="so-head">
      <h2><em>Other</em> services</h2>
      <span className="num">04 — Through certified partners</span>
    </div>
    <div className="so-grid">
      {OTHER.map((s) => (
        <div key={s.t} className="so-cell">
          <h3>{s.t}</h3>
          <p>{s.d}</p>
        </div>
      ))}
    </div>
  </section>
);

const SvcPartners = () => (
  <section className="ed-section" style={{ background: "var(--paper)" }}>
    <style>{`
      .pt-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 0; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); }
      .pt-cell { padding: 36px 24px; border-right: 1px solid var(--line); text-align: center; }
      .pt-cell:last-child { border-right: 0; }
      .pt-cell .b { font-family: 'Fraunces', serif; font-weight: 400; font-size: 28px; letter-spacing: -0.01em; margin-bottom: 10px; }
      .pt-cell .b em { font-style: italic; color: var(--clay); }
      .pt-cell .s { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--mute); letter-spacing: 0.14em; text-transform: uppercase; }
      @media (max-width: 960px) { .pt-grid { grid-template-columns: 1fr 1fr; } }
    `}</style>
    <SectionHead
      num="05"
      eyebrow="Certified partners"
      title='We don&rsquo;t <em>improvise</em><br/>on materials.'
      right="Long-term partnerships with the people who make the stuff. That's how we hold a ten-year guarantee without breaking a sweat."
    />
    <div className="pt-grid">
      <div className="pt-cell"><div className="b">Daikin</div><div className="s">Air conditioning</div></div>
      <div className="pt-cell"><div className="b">Legrand</div><div className="s">Electrical</div></div>
      <div className="pt-cell"><div className="b">Porcelanosa</div><div className="s">XTone · Fitwall</div></div>
      <div className="pt-cell"><div className="b">Knauf</div><div className="s">Plasterboard</div></div>
      <div className="pt-cell"><div className="b">Saint-Gobain</div><div className="s">Insulation · glass</div></div>
    </div>
  </section>
);

function ServicesDetails() {
  return (
    <main className="page-enter">
      <PageHero
        eyebrow="Services · 03 of 06"
        title={<>What we <em>actually</em> do<span style={{ color: "var(--clay)" }}>.</span></>}
        lede="Six trades in-house, two premium specialities, and a short list of partners for everything else. Concept to completion through a single project lead."
        meta={[
          <><strong>6</strong> Core trades, in-house</>,
          <><strong>2</strong> Premium · Porcelanosa</>,
          <><strong>10y</strong> Warranty on every build</>,
        ]}
      />
      <SvcIndex />
      <SvcDetail />
      <SvcPremium />
      <SvcOther />
      <SvcPartners />
      <Marquee items={["Plastering", "Electrical", "Plumbing", "Tiling & marble", "Painting", "False ceilings", "XTone", "Fitwall", "Daikin", "Legrand"]} />
    </main>
  );
}

export default ServicesDetails;
